import { useLayoutEffect, useRef, useState } from 'react';
import { Progress } from 'reactstrap';
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'src/componentes/dropdown';
import { capitalizeFirstLetter } from 'src/utils/string';
import LucideIcon from 'src/componentes/lucide-icon';
import Avatar from 'src/componentes/avatar';
import formatarData from 'src/utils/formatarData';
import Checklist from 'src/componentes/CheckList';
import Badge from 'src/componentes/Badge';
import Tooltip from 'src/componentes/tooltip';
import { CabecalhoColunaChecklist } from 'src/componentes/checklist/ordenacaoChecklist';

const inlineOverflowBadgeStyle = {
  background: '#E4E4E7',
  color: '#71717A',
  fontSize: 12,
  flexShrink: 0,
};

const computeVisibleTagCount = (maxWidth, tags) => {
  if (!tags?.length) return 0;
  if (maxWidth <= 0) return 0;

  const root = document.createElement('div');
  root.style.cssText = [
    'position:fixed',
    'left:-9999px',
    'top:0',
    'display:flex',
    'flex-direction:row',
    'align-items:center',
    'gap:4px',
    'flex-wrap:nowrap',
    'pointer-events:none',
    'visibility:hidden',
    'max-width:none',
    `font-family:${getComputedStyle(document.body).fontFamily}`,
    'font-size:12px',
  ].join(';');

  const tagSpan = (tag) => {
    const el = document.createElement('span');
    el.textContent = tag.descricao || '';
    el.style.cssText = [
      'display:inline-block',
      'white-space:nowrap',
      'font-size:12px',
      'line-height:1',
      'padding:0.35em 0.65em',
      'border-radius:10rem',
      `background:${tag.cor || '#6c757d'}`,
      'color:#fff',
    ].join(';');
    return el;
  };

  const overflowSpan = (n) => {
    const el = document.createElement('span');
    el.textContent = `+${n}`;
    el.style.cssText = [
      'display:inline-block',
      'white-space:nowrap',
      'font-size:12px',
      'line-height:1',
      'padding:0.35em 0.65em',
      'border-radius:10rem',
      'background:#E4E4E7',
      'color:#71717A',
    ].join(';');
    return el;
  };

  document.body.appendChild(root);

  let lo = 0;
  let hi = tags.length;
  let best = 0;

  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    root.innerHTML = '';
    for (let i = 0; i < mid; i++) {
      root.appendChild(tagSpan(tags[i]));
    }
    if (mid < tags.length) {
      root.appendChild(overflowSpan(tags.length - mid));
    }
    const w = root.scrollWidth;
    if (w <= maxWidth + 1) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }

  if (best === 0 && tags.length > 0) {
    root.innerHTML = '';
    root.appendChild(tagSpan(tags[0]));
    if (tags.length > 1) {
      root.appendChild(overflowSpan(tags.length - 1));
    }
    const singleTagWidth = root.scrollWidth;
    document.body.removeChild(root);
    return singleTagWidth <= maxWidth + 1 ? 1 : 0;
  }

  document.body.removeChild(root);
  return best;
};

const InlineStatusTags = ({ tags }) => {
  const outerRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(() => tags?.length ?? 0);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    if (!outer) return;

    if (!tags?.length) {
      setVisibleCount(0);
      return;
    }

    const measureFit = () => {
      const maxWidth = outer.clientWidth;
      if (maxWidth <= 0) return;
      setVisibleCount(computeVisibleTagCount(maxWidth, tags));
    };

    measureFit();
    const ro = new ResizeObserver(measureFit);
    ro.observe(outer);
    return () => ro.disconnect();
  }, [tags]);

  if (!tags?.length) return null;

  const hiddenTags = tags.slice(visibleCount);
  const overflowHintText = hiddenTags
    .map((t) => t.descricao)
    .filter(Boolean)
    .join('\n');

  return (
    <div
      ref={outerRef}
      style={{ minWidth: 0, width: '100%', overflow: 'hidden' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          flexWrap: 'nowrap',
        }}
      >
        {tags.slice(0, visibleCount).map((t, i) => (
          <Badge
            key={t.id ?? i}
            pill
            className="text-white"
            style={{
              background: t.cor,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: 12,
              minWidth: 0,
              flexShrink: visibleCount === 1 ? 1 : 0,
              maxWidth: visibleCount === 1 ? '100%' : undefined,
            }}
          >
            {t.descricao}
          </Badge>
        ))}
        {visibleCount < tags.length && (
          <Tooltip
            text={
              overflowHintText && (
                <span style={{ whiteSpace: 'pre-line' }}>
                  {overflowHintText}
                </span>
              )
            }
            maxWidth={320}
            textAlign="left"
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                cursor: 'help',
                flexShrink: 0,
                position: 'relative',
                zIndex: 2,
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              <Badge
                pill
                style={{ ...inlineOverflowBadgeStyle, cursor: 'help' }}
              >
                +{tags.length - visibleCount}
              </Badge>
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

const inlineColWidths = (showTags, showTarefasColumn = true) => {
  if (showTags && showTarefasColumn) {
    return {
      desc: '24%',
      resp: '17%',
      data: '9%',
      tags: '22%',
      tarefas: '14%',
      menu: '14%',
    };
  }
  if (showTags) {
    return { desc: '29%', resp: '21%', data: '12%', tags: '30%', menu: '8%' };
  }
  if (showTarefasColumn) {
    return {
      desc: '42%',
      resp: '24%',
      data: '14%',
      tarefas: '12%',
      menu: '8%',
    };
  }
  return { desc: '49%', resp: '29%', data: '18%', menu: '4%' };
};

const DEFAULT_ITEM_COLUMN_LABEL_KEY = {
  iniciativas: 'iniciativa',
};

const renderInlineHeader = ({
  itens,
  value,
  intl,
  resources,
  labelKey,
  disabled,
  adicionarNovo,
  onAdd,
  itemColumnLabelKey = 'iniciativa',
  showTags = true,
  showSummaryHeader = true,
  showTarefasColumn = true,
  ordenacao = null,
}) => {
  const cols = inlineColWidths(showTags, showTarefasColumn);
  const temItens = itens?.length > 0;

  return (
    <>
      {temItens && !adicionarNovo && (
        <div
          className="d-flex justify-content-end align-items-center mb-1"
          style={{ gap: 16 }}
        >
          {!showSummaryHeader && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#A1A1AA', flexShrink: 0, lineHeight: 1 }}>
                {value}%
              </span>
              <Progress
                style={{
                  width: 'clamp(100px, 20vw, 240px)',
                  height: 8,
                  marginTop: 0,
                }}
                value={value}
              />
            </div>
          )}
          <a
            className="text-primary"
            tabIndex={0}
            style={{
              textDecoration: 'none',
              marginTop: 8,
              marginBottom: 8,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
            }}
            onClick={() => {
              if (!disabled) onAdd();
            }}
          >
            {capitalizeFirstLetter(intl.formatMessage({ id: 'adicionar' }))}
          </a>
        </div>
      )}
      {showSummaryHeader && temItens && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
            alignItems: 'center',
            fontSize: 16,
            fontWeight: 600,
            color: '#09090B',
            marginBottom: 24,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              fontWeight: 700,
              justifySelf: 'start',
            }}
          >
            <span>
              {capitalizeFirstLetter(intl.formatMessage({ id: labelKey }))}
            </span>
            <span style={{ marginLeft: 10 }}>
              {itens && itens.filter((a) => a.concluida === true).length}/
              {itens && itens.length}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: 'clamp(160px, 38vw, 420px)',
              justifySelf: 'center',
            }}
          >
            <span style={{ color: '#A1A1AA', flexShrink: 0, lineHeight: 1 }}>
              {value}%
            </span>
            <Progress
              style={{ flex: 1, minWidth: 64, height: 8, marginTop: 0 }}
              value={value}
            />
          </div>
          <div aria-hidden style={{ minWidth: 0 }} />
        </div>
      )}
      {temItens && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '5px 6px',
            borderTop: '1px solid #E4E4E7',
            borderBottom: '1px solid #E4E4E7',
            color: '#404040',
            fontFamily: 'Nunito, sans-serif',
            fontSize: '16px',
            fontWeight: 600,
            lineHeight: '16px',
          }}
        >
          <CabecalhoColunaChecklist
            rotulo={capitalizeFirstLetter(
              intl.formatMessage({ id: itemColumnLabelKey })
            )}
            campo="descricao"
            ordenacao={ordenacao}
            style={{ width: cols.desc, paddingRight: 6 }}
          />
          <CabecalhoColunaChecklist
            rotulo={capitalizeFirstLetter(resources.responsavel)}
            campo="responsavel"
            ordenacao={ordenacao}
            style={{ width: cols.resp, paddingRight: 6 }}
          />
          <CabecalhoColunaChecklist
            rotulo={capitalizeFirstLetter(intl.formatMessage({ id: 'data' }))}
            campo="data"
            ordenacao={ordenacao}
            style={{ width: cols.data, paddingRight: 6, textAlign: 'left' }}
          />
          {showTags && (
            <div style={{ width: cols.tags, paddingRight: 6 }}>
              {capitalizeFirstLetter(
                intl.formatMessage({ id: 'tags' }, { tags: resources.tags })
              )}
            </div>
          )}
          {showTarefasColumn && (
            <div
              style={{
                width: cols.tarefas,
                paddingRight: 6,
                textAlign: 'center',
              }}
            >
              {capitalizeFirstLetter(intl.formatMessage({ id: 'tarefas' }))}
            </div>
          )}
          <div style={{ width: cols.menu }} />
        </div>
      )}
    </>
  );
};

const renderInlineItem = (ctx) => {
  const {
    item,
    isDragging,
    dragHandleProps,
    intl,
    resources,
    user,
    disabled,
    permissions,
    actions,
    utils,
    showTransformarProjeto = true,
    showTags = true,
    showTarefasColumn = true,
  } = ctx;

  const cols = inlineColWidths(showTags, showTarefasColumn);
  const { style: dhStyle, ...dhRest } = dragHandleProps || {};

  const tarefas = item?.tarefas || [];
  const totalTarefas = tarefas.length;
  const concluidas = tarefas.filter((t) => t.concluida === true).length;
  const tarefasText = `${concluidas}/${totalTarefas}`;

  return (
    <div className={isDragging ? 'p-0' : 'p-0'} style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: '4px 6px',
          fontSize: 14,
          fontWeight: 500,
          color: '#09090B',
          background: isDragging ? '#f3f3f3' : undefined,
        }}
      >
        <div
          {...dhRest}
          style={{
            width: cols.desc,
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            paddingRight: 6,
            outline: 'none',
            ...(dhStyle || {}),
          }}
        >
          <input
            type="checkbox"
            disabled={disabled}
            checked={!!item.concluida}
            onChange={() => actions.checkIniciativa(item.id)}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ flexShrink: 0, marginRight: 5 }}
          />
          <Tooltip text={item.descricao}>
            <div
              className="text-primary"
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textDecoration: item.concluida ? 'line-through' : 'none',
                cursor: 'pointer',
              }}
              onClick={() => actions.handleAtivarEdicao(item)}
            >
              {item.descricao}
            </div>
          </Tooltip>
        </div>

        <div
          style={{
            width: cols.resp,
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            gap: 6,
            paddingRight: 6,
          }}
        >
          {item?.responsavel ? (
            <>
              <Avatar
                foto={item.responsavel?.fotoArquivo}
                showLabel={false}
                border={1}
                size={24}
                label={item.responsavel?.nome}
              />
              <Tooltip text={item.responsavel?.nome}>
                <div
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.responsavel?.nome}
                </div>
              </Tooltip>
            </>
          ) : (
            '-'
          )}
        </div>

        <div
          style={{
            width: cols.data,
            paddingRight: 6,
            textAlign: 'left',
            color: (() => {
              const corDataConclusao = utils.calculaCorDataConclusao(
                item.dataConclusao
              );

              return !item.concluida && corDataConclusao
                ? corDataConclusao
                : '#09090B';
            })(),
          }}
        >
          {item.dataConclusao && formatarData(user.idioma, item.dataConclusao)}
        </div>

        {showTags && (
          <div
            style={{
              width: cols.tags,
              display: 'flex',
              alignItems: 'center',
              paddingRight: 6,
              minWidth: 0,
            }}
          >
            <InlineStatusTags tags={item.tags} />
          </div>
        )}

        {showTarefasColumn && (
          <div
            style={{
              width: cols.tarefas,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              paddingRight: 6,
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (actions?.handleAbrirTarefasModal) {
                actions.handleAbrirTarefasModal(item);
              }
            }}
          >
            <Badge
              pill
              color="secondary"
              style={{
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                padding: '4px 10px',
              }}
            >
              {tarefasText}
            </Badge>
          </div>
        )}

        <div
          style={{
            width: cols.menu,
            display: 'flex',
            justifyContent: 'flex-end',
            flexShrink: 0,
          }}
        >
          <UncontrolledDropdown group>
            <DropdownToggle
              color="default"
              size="sm"
              toolbar
              icon="dots-vertical"
            />
            <DropdownMenu>
              {(permissions == null || permissions.allowEdit) && (
                <DropdownItem onClick={() => actions.handleAtivarEdicao(item)}>
                  <LucideIcon icon="pencil" />
                  {capitalizeFirstLetter(intl.formatMessage({ id: 'editar' }))}
                </DropdownItem>
              )}

              <DropdownItem
                onClick={() => actions.handleAbrirTarefasModal?.(item)}
              >
                <LucideIcon icon="ClipboardList" />
                {capitalizeFirstLetter(intl.formatMessage({ id: 'tarefas' }))}
              </DropdownItem>
              {showTransformarProjeto && (
                <DropdownItem
                  onClick={() => actions.handleTransformarProjeto(item)}
                >
                  <LucideIcon icon="wrap" />
                  {capitalizeFirstLetter(
                    intl.formatMessage(
                      { id: 'transformarEmProjeto' },
                      { projeto: resources.projeto }
                    )
                  )}
                </DropdownItem>
              )}
              {(permissions == null || permissions.allowDelete) && (
                <DropdownItem
                  onClick={() => actions.handleRemoveIniciativa(item.id)}
                >
                  <LucideIcon icon="delete" />
                  {capitalizeFirstLetter(intl.formatMessage({ id: 'excluir' }))}
                </DropdownItem>
              )}
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
      </div>

      {utils.retornaRascunhoAlterado(item.id) != null && (
        <div style={{ display: 'flex', marginTop: 2, padding: '0 6px 4px' }}>
          <span>
            {intl.formatMessage({ id: 'voceTemEdicoesNaoSalvasNesteCampo' })}
          </span>
          <a
            className="text-primary"
            tabIndex="-1"
            onClick={() => actions.handleAtivarEdicao(item)}
            style={{ marginLeft: 5 }}
          >
            {intl.formatMessage({ id: 'exibirEdicao' })}
          </a>
          <a
            className="text-primary"
            tabIndex="-1"
            onClick={() => actions.close(item.id)}
            style={{ marginLeft: 5 }}
          >
            {intl.formatMessage({ id: 'descartar' })}
          </a>
        </div>
      )}
    </div>
  );
};

const InlineChecklist = ({
  addButtonLabelKey = 'iniciativas',
  itemColumnLabelKey,
  showTransformarProjeto = true,
  showTags = true,
  showSummaryHeader = true,
  acaoId,
  showTarefasColumn = true,
  ...props
}) => {
  const resolvedItemColumnLabelKey =
    itemColumnLabelKey ??
    DEFAULT_ITEM_COLUMN_LABEL_KEY[addButtonLabelKey] ??
    addButtonLabelKey;

  return (
    <Checklist
      {...props}
      acaoId={acaoId}
      showTags={showTags}
      salvarItemLabelKey={resolvedItemColumnLabelKey}
      okrInlineLayout
      renderOkrInlineHeader={(ctx) =>
        renderInlineHeader({
          ...ctx,
          itemColumnLabelKey: resolvedItemColumnLabelKey,
          showTags,
          showSummaryHeader,
          showTarefasColumn,
        })
      }
      renderOkrInlineItem={(ctx) =>
        renderInlineItem({
          ...ctx,
          showTransformarProjeto,
          showTags,
          showTarefasColumn,
        })
      }
    />
  );
};

export default InlineChecklist;
